import { motion } from "framer-motion";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";

interface CityComparisonProps {
  cityName: string;
  driveTime: string;
}

const comparisonData = [
  { critere: "Localisation", autosur: "accessible", autosurText: "", others: "15-20 min", autosurIcon: "check", othersIcon: "warn" },
  { critere: "Parking gratuit", autosur: "Oui", others: "Souvent payant", autosurIcon: "check", othersIcon: "cross" },
  { critere: "Expertise locale", autosur: "20+ ans", others: "Variable", autosurIcon: "check", othersIcon: "warn" },
  { critere: "Tarifs en ligne", autosur: "73€", others: "73-85€", autosurIcon: "check", othersIcon: "warn" },
  { critere: "RDV en ligne 24/7", autosur: "Oui", others: "Parfois limité", autosurIcon: "check", othersIcon: "warn" },
  { critere: "Résultat immédiat", autosur: "30-45 min", others: "Parfois 1-2h", autosurIcon: "check", othersIcon: "warn" },
  { critere: "Avis clients", autosur: "⭐ 4.9/5 (343)", others: "⭐ 3.5-4.5/5", autosurIcon: "check", othersIcon: "warn" },
  { critere: "Samedi matin", autosur: "9h-13h", others: "Fermé", autosurIcon: "check", othersIcon: "cross" },
];

const IconCell = ({ type }: { type: string }) => {
  if (type === "check") return <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />;
  if (type === "cross") return <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />;
  return <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />;
};

export const CityComparison = ({ cityName, driveTime }: CityComparisonProps) => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
            Pourquoi choisir notre centre plutôt qu'un autre à {cityName} ?
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            Comparez notre centre avec les autres options disponibles près de {cityName}.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 bg-autosur-navy text-white">
              <div className="p-4 font-heading font-semibold text-sm">Critère</div>
              <div className="p-4 font-heading font-semibold text-sm text-center border-l border-white/20">
                AUTOSUR
              </div>
              <div className="p-4 font-heading font-semibold text-sm text-center border-l border-white/20">
                Autres centres
              </div>
            </div>

            {/* Rows */}
            {comparisonData.map((row, index) => (
              <div
                key={row.critere}
                className={`grid grid-cols-3 ${index % 2 === 0 ? "bg-white" : "bg-gray-50"} border-t border-gray-100`}
              >
                <div className="p-4 font-heading font-semibold text-autosur-navy text-xs sm:text-sm">
                  {row.critere}
                </div>
                <div className="p-4 border-l border-gray-100 flex items-center justify-center gap-2">
                  <IconCell type={row.autosurIcon} />
                  <span className="font-body text-autosur-navy text-xs sm:text-sm font-semibold">
                    {row.critere === "Localisation" ? `${driveTime}` : row.autosur}
                  </span>
                </div>
                <div className="p-4 border-l border-gray-100 flex items-center justify-center gap-2">
                  <IconCell type={row.othersIcon} />
                  <span className="font-body text-muted-foreground text-xs sm:text-sm">
                    {row.others}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Conclusion */}
          <div className="mt-6 bg-autosur-blue/5 rounded-2xl p-6 border border-autosur-blue/20 text-center">
            <p className="font-heading font-bold text-autosur-navy">
              <CheckCircle className="w-5 h-5 text-green-500 inline mr-2" />
              Meilleur rapport qualité/prix/localisation à {cityName}
            </p>
            <p className="font-body text-muted-foreground text-sm mt-1">
              343 avis clients ⭐ 4.9/5 – Marque Préférée des Français
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
